/**
 * Onboarding API Page
 * Handles onboarding requests from landing page
 * This page acts as an API endpoint for the landing site
 */

import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { supabase } from '../services/supabase'
import { createOrganizationAccount, initializePayment } from '../services/onboardingAPI'
import LoadingSpinner from '../components/LoadingSpinner'
import { motion } from 'framer-motion'

const OnboardingAPI = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState('processing')
  const [message, setMessage] = useState('Processing your request...')

  useEffect(() => {
    const action = searchParams.get('action')
    const source = searchParams.get('source')
    const ref = searchParams.get('reference')

    if (action === 'create' && source === 'landing') {
      handleOnboardingCreate()
    } else if (action === 'verify' && ref) {
      handlePaymentVerify(ref)
    } else {
      setStatus('error')
      setMessage('Invalid request')
    }
  }, [searchParams])

  const handleOnboardingCreate = async () => {
    try {
      setMessage('Creating your account...')
      
      // Try to get data from URL parameter first (works across domains)
      const urlParams = new URLSearchParams(window.location.search)
      let onboardingData = null
      
      // Check URL parameter (base64 encoded)
      const encodedData = urlParams.get('data')
      if (encodedData) {
        try {
          const decodedData = decodeURIComponent(atob(encodedData))
          onboardingData = JSON.parse(decodedData)
        } catch (e) {
          console.error('Error decoding URL data:', e)
        }
      }
      
      // Fallback to sessionStorage (if same domain)
      if (!onboardingData) {
        const data = sessionStorage.getItem('landing_onboarding_data')
        if (data) {
          onboardingData = JSON.parse(data)
        }
      }
      
      if (!onboardingData) {
        throw new Error('Onboarding data not found. Please start over.')
      }
      
      // Create user account and submit organization request
      const result = await createOrganizationAccount(onboardingData)
      
      if (!result.success) {
        throw new Error(result.error)
      }

      // Request submitted successfully
      setMessage(result.data.message || 'Your organization request has been submitted successfully!')
      setStatus('success')
      
      // Store request ID for reference
      if (result.data.requestId) {
        sessionStorage.setItem('onboarding_request_id', result.data.requestId)
      }
      
      // Clean up onboarding data from sessionStorage and URL
      try {
        sessionStorage.removeItem('landing_onboarding_data')
      } catch (e) {
        // Ignore if sessionStorage not available
      }
      
      // Clean up URL parameter by removing data
      try {
        const url = new URL(window.location.href)
        url.searchParams.delete('data')
        window.history.replaceState({}, '', url.toString())
      } catch (e) {
        // Ignore if URL manipulation fails
      }

      // Redirect to login/dashboard after 5 seconds
      setTimeout(() => {
        navigate('/login?request_submitted=true')
      }, 5000)
    } catch (error) {
      console.error('Onboarding error:', error)
      setStatus('error')
      setMessage(error.message || 'Failed to process onboarding')
      
      // Clean up onboarding data
      try {
        sessionStorage.removeItem('landing_onboarding_data')
      } catch (e) {
        // Ignore if sessionStorage not available
      }
      
      // Clean up URL parameter
      try {
        const url = new URL(window.location.href)
        url.searchParams.delete('data')
        window.history.replaceState({}, '', url.toString())
      } catch (e) {
        // Ignore if URL manipulation fails
      }
    }
  }

  const handlePaymentVerify = async (reference) => {
    try {
      setMessage('Verifying your payment...')
      
      const organizationId = sessionStorage.getItem('onboarding_org_id')
      if (!organizationId) {
        throw new Error('Organization ID not found')
      }

      // Verify payment via Supabase Edge Function
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const { data: { session } } = await supabase.auth.getSession()

      const response = await fetch(`${supabaseUrl}/functions/v1/paystack-payment/verify/${reference}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session?.access_token || ''}`,
        },
      })

      if (!response.ok) {
        throw new Error('Payment verification failed')
      }

      const verifyData = await response.json()

      if (verifyData.status === 'success') {
        // Update subscription
        const { error: updateError } = await supabase
          .from('organization_subscriptions')
          .upsert({
            organization_id: organizationId,
            tier_id: verifyData.metadata?.tier_id,
            status: 'active',
            payment_provider: 'paystack',
            payment_provider_subscription_id: reference,
          })

        if (updateError) {
          console.error('Subscription update error:', updateError)
        }

        setStatus('success')
        setMessage('Payment verified! Redirecting to your dashboard...')
        
        sessionStorage.removeItem('onboarding_org_id')
        
        setTimeout(() => {
          navigate('/dashboard?onboarding=complete')
        }, 2000)
      } else {
        throw new Error('Payment verification failed')
      }
    } catch (error) {
      console.error('Verification error:', error)
      setStatus('error')
      setMessage(error.message || 'Payment verification failed. Please contact support.')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-md"
      >
        {status !== 'success' && <LoadingSpinner />}
        <p className="mt-6 text-lg text-gray-700 dark:text-gray-300">{message}</p>
        {status === 'success' && (
          <div className="mt-6 space-y-4">
            <div className="p-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
              <p className="text-green-800 dark:text-green-200 mb-4">
                Your account has been created and your organization request has been submitted. 
                Our team will review your request and contact you soon.
              </p>
              <p className="text-sm text-green-700 dark:text-green-300">
                You'll be redirected to the login page in a moment...
              </p>
            </div>
            <a
              href="/login"
              className="inline-block px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
            >
              Go to Login
            </a>
          </div>
        )}
        {status === 'error' && (
          <div className="mt-6 space-y-4">
            <a
              href="https://naturassist.com"
              className="inline-block px-6 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
            >
              Return to Home
            </a>
            <a
              href="mailto:support@naturassist.com"
              className="block text-primary-600 dark:text-primary-400 hover:underline mt-4"
            >
              Contact Support
            </a>
          </div>
        )}
      </motion.div>
    </div>
  )
}

export default OnboardingAPI

